import { useId, useRef, useState } from 'react';
import { quantityConverter } from '../../common';
import Slider from 'rc-slider';
import PortioningValuesBox from './PortioningValuesBox';

// Style
import 'rc-slider/assets/index.css';
import './PortionSlider.css';

// TO DO: uncomment that with the new logic when is decided
// const isProductCutWhole = item => item?.variant?.optionValues.some( optionValue => optionValue.id === 1 )
const isProductCutWhole = () => false;

export const getPortionLimits = (item, quantity) => {
  const minQuantity = isProductCutWhole(item)
    ? item.variant.defaultQuantity / 2
    : Math.max(Math.ceil(quantity / 30 / 100) * 100, 500);
  const maxQuantity = quantity ? Math.min(10000, quantity) : 1000;

  return [minQuantity, maxQuantity];
};

export const isItemPortioned = item =>
  !!item?.portioningSize && item.portioningSize !== item.quantity;

const PortionSlider = ({ portion, item, onCommit, itemQuantity }) => {
  const [draggingPortion, setDraggingPortion] = useState(null);
  // rc-slider ends a drag with the last rendered value, which can lag behind onChange
  const changedPortionRef = useRef(null);
  const labelId = useId();

  const incrementStep = isProductCutWhole(item)
    ? item.variant.defaultQuantity / 2
    : 250;

  const [minQuantity, maxQuantity] = getPortionLimits(item, itemQuantity);

  const unitForQuantity = item?.variant.unitForQuantity;
  const shownPortion = draggingPortion ?? portion;
  const sliderValue =
    draggingPortion ?? (portion >= minQuantity ? portion : minQuantity);
  const stepText = quantityConverter(incrementStep, unitForQuantity);

  const handleChange = value => {
    changedPortionRef.current = value;
    setDraggingPortion(value);
  };

  const handleAfterChange = () => {
    const changedPortion = changedPortionRef.current;
    changedPortionRef.current = null;
    setDraggingPortion(null);
    if (changedPortion != null) onCommit(changedPortion);
  };

  const handleStep = direction => {
    const steps = (sliderValue - minQuantity) / incrementStep;
    const nextSteps =
      direction > 0 ? Math.floor(steps) + 1 : Math.ceil(steps) - 1;
    const nextPortion = Math.min(
      maxQuantity,
      Math.max(minQuantity, minQuantity + nextSteps * incrementStep),
    );
    if (nextPortion !== portion) onCommit(nextPortion);
  };

  return (
    <div className="portion-slider-container">
      <div className="portion-slider-controls">
        <div className="portion-slider-header">
          <div className="portion-slider-value">
            <span id={labelId} className="portion-slider-label">
              Tamanho de cada porção
            </span>
            <output className="portion-slider-amount" aria-live="polite">
              {shownPortion
                ? quantityConverter(shownPortion, unitForQuantity)
                : '- -'}
            </output>
          </div>
          <div className="portion-slider-stepper">
            <button
              type="button"
              aria-label={`Diminuir ${stepText}`}
              disabled={sliderValue <= minQuantity}
              onClick={() => handleStep(-1)}
            >
              <i className="fa fa-minus" aria-hidden="true" />
            </button>
            <button
              type="button"
              aria-label={`Aumentar ${stepText}`}
              disabled={sliderValue >= maxQuantity}
              onClick={() => handleStep(1)}
            >
              <i className="fa fa-plus" aria-hidden="true" />
            </button>
          </div>
        </div>

        <Slider
          className="portion-slider"
          min={minQuantity}
          max={maxQuantity}
          step={incrementStep}
          value={sliderValue}
          onChange={handleChange}
          onAfterChange={handleAfterChange}
          ariaLabelledByForHandle={labelId}
          ariaValueTextFormatterForHandle={value =>
            quantityConverter(value, unitForQuantity)
          }
          trackStyle={{ backgroundColor: '#eb5b1e' }}
          handleStyle={{ borderColor: '#ce3c25' }}
        />
        <div className="portion-slider-range" aria-hidden="true">
          <span>{quantityConverter(minQuantity, unitForQuantity)}</span>
          <span>{quantityConverter(maxQuantity, unitForQuantity)}</span>
        </div>
      </div>

      {Number(shownPortion) > 0 && (
        <PortioningValuesBox
          quantity={itemQuantity}
          portioningSize={shownPortion}
        />
      )}
    </div>
  );
};

export default PortionSlider;
